import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyStadiums, setStadium } from '../api/stadiums';
import { fetchMyCosmetics, setCosmetic } from '../api/cosmetics';
import GLOBALS from '../config/globals';
import { toast } from 'react-toastify';

const CustomizationsPage = () => {
    const [stadiumData, setStadiumData] = useState({ stadiums: [], stadiumMode: 'default', selectedStadiumID: null });
    const [cosmeticData, setCosmeticData] = useState({ cosmetics: [], preferences: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadCustomization = async () => {
            try {
                const [stadiums, cosmetics] = await Promise.all([fetchMyStadiums(), fetchMyCosmetics()]);
                setStadiumData(stadiums);
                setCosmeticData(cosmetics);
            } catch (err) {
                console.error('Error loading customization:', err);
            }
            setLoading(false);
        };
        loadCustomization();
    }, []);

    const stadiumSelectValue = () => {
        if (stadiumData.stadiumMode === GLOBALS.STADIUM_MODE.RANDOM) return 'random';
        if (stadiumData.stadiumMode === GLOBALS.STADIUM_MODE.SELECTED && stadiumData.selectedStadiumID) {
            return `selected:${stadiumData.selectedStadiumID}`;
        }
        return 'default';
    };

    const handleStadiumSelect = async (value) => {
        const prev = stadiumData;
        let mode, stadiumID;
        if (value === 'default') {
            mode = GLOBALS.STADIUM_MODE.DEFAULT;
        } else if (value === 'random') {
            mode = GLOBALS.STADIUM_MODE.RANDOM;
        } else {
            mode = GLOBALS.STADIUM_MODE.SELECTED;
            stadiumID = value.replace('selected:', '');
        }
        setStadiumData(s => ({ ...s, stadiumMode: mode, selectedStadiumID: stadiumID || null }));
        try {
            await setStadium({ stadiumMode: mode, selectedStadiumID: stadiumID || undefined });
        } catch (err) {
            setStadiumData(prev);
            toast.error('Failed to update stadium: ' + err.message);
        }
    };

    const cosmeticSelectValue = (type) => {
        const pref = cosmeticData.preferences.find(p => p.type === type);
        const currentMode = pref?.mode || GLOBALS.COSMETIC_MODE.DEFAULT;
        if (currentMode === GLOBALS.COSMETIC_MODE.RANDOM) return 'random';
        if (currentMode === GLOBALS.COSMETIC_MODE.SELECTED && pref?.cosmeticID) {
            return `selected:${pref.cosmeticID}`;
        }
        return 'default';
    };

    const handleCosmeticSelect = async (type, value) => {
        let mode, cosmeticID;
        if (value === 'default') {
            mode = GLOBALS.COSMETIC_MODE.DEFAULT;
        } else if (value === 'random') {
            mode = GLOBALS.COSMETIC_MODE.RANDOM;
        } else {
            mode = GLOBALS.COSMETIC_MODE.SELECTED;
            cosmeticID = value.replace('selected:', '');
        }
        try {
            await setCosmetic({ type, mode, cosmeticID: cosmeticID || undefined });
            const updated = await fetchMyCosmetics();
            setCosmeticData(updated);
        } catch (err) {
            toast.error('Failed to update cosmetic: ' + err.message);
        }
    };

    const ownedCosmetics = (type) => cosmeticData.cosmetics
        .filter(uc => uc.Cosmetic?.type === type)
        .map(uc => uc.Cosmetic);

    if (loading) return <div className="container"><p>Loading...</p></div>;

    const stadiums = stadiumData.stadiums.map(us => us.Stadium || us);

    return (
        <div className="container">
            <div className="banner-header">
                <Link to="/account" className="back-button">&larr;</Link>
                <h1>My Customizations</h1>
            </div>

            <div className="customization-subsection">
                Stadium:
                {' '}
                <select value={stadiumSelectValue()} onChange={e => handleStadiumSelect(e.target.value)}>
                    <option value="default">Default (Classic)</option>
                    <option value="random">Random</option>
                    {stadiums.map(stadium => (
                        <option key={stadium.stadiumID} value={`selected:${stadium.stadiumID}`}>
                            {stadium.name}{stadium.rarity ? ` (${stadium.rarity})` : ''}
                        </option>
                    ))}
                </select>
                {stadiums.length === 0 && (
                    <span className="text-muted"> — No stadiums earned yet</span>
                )}
            </div>

            {['dice', 'meeple'].map(type => {
                const owned = ownedCosmetics(type);
                return (
                    <div key={type} className="customization-subsection">
                        {type === 'dice' ? 'Dice' : 'Meeple'}:
                        {' '}
                        <select value={cosmeticSelectValue(type)} onChange={e => handleCosmeticSelect(type, e.target.value)}>
                            <option value="default">Default</option>
                            <option value="random">Random</option>
                            {owned.map(c => (
                                <option key={c.cosmeticID} value={`selected:${c.cosmeticID}`}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                        {owned.length === 0 && (
                            <span className="text-muted"> — Earn from campaigns &amp; achievements</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CustomizationsPage;
